<template>
  <q-dialog v-model="visible" full-height position="right">
    <q-card style="width: 900px; max-width: 90vw">
      <q-toolbar class="auto-height horizontal-line">
        <q-toolbar-title class="q-pa-sm"> リッチメニュー表示条件設定 </q-toolbar-title>
        <div class="row full-width justify-end q-pr-md">
          <TestModeSelector v-model="isTestMode" :isHideCopy="true" />
        </div>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>
      <q-card-section class="q-pa-none">
        <CLoading :loading="loading" />
        <div class="row max-height-dialog horizontal-line q-pt-md q-px-md">
          <div class="col-4 vertical-line" style="max-height: calc(100vh - 140px); overflow-y: auto">
            <q-expansion-item
              dense
              dense-toggle
              switch-toggle-side
              v-model="expandedDefault"
              class="cus-expansion"
              expand-icon-class="text-grey q-pr-none"
            >
              <template v-slot:header>
                <div class="row full-width items-center">
                  <div class="text-caption text-grey">デフォルト</div>
                  <div class="col horizontal-line-dashed"></div>
                </div>
              </template>
              <div class="row items-center btn-cancel q-mr-md" v-if="!defaultRichMenu._id">
                <div class="col-2"></div>
                <div class="col text-black q-py-xs">
                  <div class="text-caption">リッチメニュー</div>
                  <div class="text-subtitle2">No Data</div>
                </div>
                <div class="col-2">
                  <q-icon size="20px" color="red" name="warning"></q-icon>
                </div>
              </div>
              <div
                class="row items-center btn-cancel q-mr-md q-mb-sm"
                :class="selectedRichMenuSetting._id === defaultRichMenu._id ? 'selected-item' : ''"
                @click="onSelectDefaultRichMenu()"
                v-else
              >
                <div class="col-2"></div>
                <div class="col text-black q-py-xs">
                  <div class="text-caption">リッチメニュー</div>
                  <div class="text-subtitle2">{{ defaultRichMenu.name }}</div>
                </div>
              </div>
            </q-expansion-item>
            <q-expansion-item
              dense
              dense-toggle
              switch-toggle-side
              v-model="expandedAudience"
              class="cus-expansion q-mt-md"
              expand-icon-class="text-grey q-pr-none"
            >
              <template v-slot:header>
                <div class="row full-width items-center">
                  <div class="text-caption text-grey">オーディエンス別</div>
                  <div class="col horizontal-line-dashed"></div>
                </div>
              </template>
              <div>
                <div class="text-subtitle2 text-center q-mr-md q-mb-sm" style="background-color: #ff737c1a">
                  優先度：高い
                </div>
                <draggable :list="priorityRichMenus" itemKey="_id">
                  <template #item="{ element, index }">
                    <div
                      class="row items-center btn-cancel q-mr-md q-mb-sm"
                      :class="selectedRichMenuSetting._id === element._id ? 'selected-item' : ''"
                      :key="index"
                      @click="onSelectRichMenu(element)"
                    >
                      <div class="col-2 flex justify-center">
                        <q-icon name="drag_indicator" color="black" size="sm" />
                      </div>
                      <div class="col text-black q-py-xs">
                        <div class="text-caption">リッチメニュー</div>
                        <div class="text-subtitle2">{{ element.name }}</div>
                      </div>
                    </div>
                  </template>
                </draggable>
                <div class="text-subtitle2 text-center q-mr-md q-my-sm" style="background-color: #63a9e51a">
                  優先度：低い
                </div>
              </div>
            </q-expansion-item>
          </div>
          <div class="col" style="max-height: calc(100vh - 140px); overflow-y: auto">
            <RichMenuConditionSetting
              v-model="selectedRichMenuSetting"
              :defaultRichMenu="defaultRichMenu"
              :isTestMode="isTestMode"
              :appId="appId"
              @setDefaultRichMenu="onSetRichMenuDefault"
              :key="selectedRichMenuSetting._id"
            />
          </div>
        </div>
        <div class="row no-wrap items-center q-mt-md q-pr-md">
          <q-space />
          <div class="q-gutter-sm">
            <q-btn no-caps flat class="btn-cancel" @click="onCancel">{{ $t('cancel') }} </q-btn>
            <q-btn no-caps class="btn-save" :disable="!form.prod_detail?.default_rich_menu_id" @click="onSubmit">
              {{ $t('save') }}
            </q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { IRichMenu, IRichMenuAudienceSetting, IRichMenuConditionSetting } from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'
import draggable from 'vuedraggable'
import RichMenuConditionSetting from '@/components/rich-menus/selectors/RichMenuConditionSetting.vue'
import { ACTION_RICH_MENU_CONDITION_SETTING } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, RichMenuConditionSetting, TestModeSelector, draggable },
  directives: { maska },
  emits: ['update:closeModal', 'onUpdateSettings'],
})
export default class ConditionSettingModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  settingData!: IRichMenuConditionSetting

  @Prop()
  activeRichMenus!: IRichMenu[]

  @Prop()
  testRichMenus!: IRichMenu[]

  @Prop()
  appId!: string

  loading = false
  isTestMode = false
  expandedDefault = true
  expandedAudience = true
  defaultRichMenu: IRichMenu = {}
  priorityRichMenus: IRichMenuAudienceSetting[] = []
  selectedRichMenuSetting: IRichMenuAudienceSetting = {}
  form: IRichMenuConditionSetting = {
    _id: '',
    app_id: '',
    display_setting: false,
    prod_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [
        {
          _id: '',
          name: '',
          image_url: '',
          audience: {
            audience_or: [],
            audience_not: [],
          },
        },
      ],
    },
    test_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [
        {
          _id: '',
          name: '',
          image_url: '',
          audience: {
            audience_or: [],
            audience_not: [],
          },
        },
      ],
    },
  }

  onSelectRichMenu(richMenu: IRichMenuAudienceSetting) {
    this.selectedRichMenuSetting = richMenu
  }

  onSetRichMenuDefault(id) {
    if (this.isTestMode && this.form.test_detail) {
      this.form.test_detail.default_rich_menu_id = id
      this.defaultRichMenu =
        this.testRichMenus.find((item) => item._id === this.form.test_detail?.default_rich_menu_id) ?? {}
    } else if (!this.isTestMode && this.form.prod_detail) {
      this.form.prod_detail.default_rich_menu_id = id
      this.defaultRichMenu =
        this.activeRichMenus.find((item) => item._id === this.form.prod_detail?.default_rich_menu_id) ?? {}
    }
    this.handleRichMenus()
    this.onSelectDefaultRichMenu()
  }

  onSelectDefaultRichMenu() {
    this.selectedRichMenuSetting = {
      _id: this.defaultRichMenu._id,
      chat_bar_text: this.isTestMode
        ? this.defaultRichMenu.test_detail?.chat_bar_text
        : this.defaultRichMenu.prod_detail?.chat_bar_text,
      name: this.defaultRichMenu.name,
      image_url: this.isTestMode
        ? this.defaultRichMenu.test_detail?.rich_menu_content?.[0]?.image_url
        : this.defaultRichMenu.prod_detail?.rich_menu_content?.[0]?.image_url,
      audience: {
        audience_or: [],
        audience_not: [],
      },
    }
  }

  onUpdateFormData() {
    if (this.isTestMode && this.form.test_detail) {
      this.form.test_detail.priority_rich_menu = this.priorityRichMenus
    } else if (!this.isTestMode && this.form.prod_detail) {
      this.form.prod_detail.priority_rich_menu = this.priorityRichMenus
    }
  }

  async onSubmit() {
    this.loading = true
    this.onUpdateFormData()
    const { dispatch } = this.$store
    let item = null
    if (this.isNew) {
      this.form.test_detail = this.form.prod_detail
      item = await dispatch(ACTION_RICH_MENU_CONDITION_SETTING.ADD, {
        ...this.form,
      })
    } else {
      item = await dispatch(ACTION_RICH_MENU_CONDITION_SETTING.UPDATE, {
        ...this.form,
      })
    }
    if (item) {
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
      this.$emit('onUpdateSettings', item)
    }
    this.loading = false
    this.visible = false
  }

  @Watch('isTestMode')
  handleRichMenus() {
    if (this.isTestMode) {
      this.defaultRichMenu =
        this.testRichMenus.find((item) => item._id === this.form.test_detail?.default_rich_menu_id) ?? {}

      this.priorityRichMenus = this.mergeRichMenuWithAudience(
        this.testRichMenus,
        this.form.test_detail?.priority_rich_menu ?? [],
        true,
        this.defaultRichMenu._id ?? ''
      )
    } else {
      this.defaultRichMenu =
        this.activeRichMenus.find((item) => item._id === this.form.prod_detail?.default_rich_menu_id) ?? {}

      this.priorityRichMenus = this.mergeRichMenuWithAudience(
        this.activeRichMenus,
        this.form.prod_detail?.priority_rich_menu ?? [],
        false,
        this.defaultRichMenu._id ?? ''
      )
    }
    if (this.priorityRichMenus.length > 0) {
      this.selectedRichMenuSetting = this.priorityRichMenus[0]
    } else if (this.defaultRichMenu._id) {
      this.onSelectDefaultRichMenu()
    } else {
      this.selectedRichMenuSetting = {
        _id: '',
        name: '',
        image_url: '',
        chat_bar_text: '',
        audience: {
          audience_or: [],
          audience_not: [],
        },
      }
    }
  }

  mergeRichMenuWithAudience(
    menus: IRichMenu[],
    audienceSettings: IRichMenuAudienceSetting[],
    isTestMode: boolean,
    defaultMenuId?: string
  ): IRichMenuAudienceSetting[] {
    const audienceMap = new Map(audienceSettings.filter((item) => item._id).map((item) => [item._id, item]))

    const filteredMenus = menus.filter((menu) => menu._id !== defaultMenuId)

    return filteredMenus.map((menu) => {
      const setting = audienceMap.get(menu._id || '')
      return {
        _id: menu._id,
        name: menu.name,
        chat_bar_text: isTestMode ? menu.test_detail?.chat_bar_text : menu.prod_detail?.chat_bar_text,
        image_url: isTestMode
          ? menu.test_detail?.rich_menu_content?.[0]?.image_url
          : menu.prod_detail?.rich_menu_content?.[0]?.image_url,
        audience: {
          audience_or: setting?.audience?.audience_or ?? [],
          audience_not: setting?.audience?.audience_not ?? [],
        },
      }
    })
  }

  async created() {
    this.form = { ...this.form, ...cloneDeep(this.settingData) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
    this.handleRichMenus()
    if (this.priorityRichMenus.length > 0) {
      this.selectedRichMenuSetting = this.priorityRichMenus[0]
    } else if (this.defaultRichMenu._id) {
      this.onSelectDefaultRichMenu()
    } else {
      this.selectedRichMenuSetting = {
        _id: '',
        name: '',
        image_url: '',
        chat_bar_text: '',
        audience: {
          audience_or: [],
          audience_not: [],
        },
      }
    }
  }
}
</script>

<style scoped lang="scss">
:deep(.cus-expansion .q-item) {
  padding-left: 0;
  padding-right: 0;
  margin-right: 16px;
}
:deep(.cus-expansion .q-item__section--avatar) {
  min-width: 20px;
}
.horizontal-line-dashed {
  border-bottom: 1px dashed #9e9e9e;
}
.selected-item {
  background-color: #7c7d701a !important;
  border-right: 16px solid #1f1f1f;
}
.max-height-dialog {
  height: calc(100vh - 120px);
  overflow-x: auto;
}
</style>
